<template>
  <div class="main-layout">

    <!-- ===================================================== -->
    <!-- SIDEBAR -->
    <!-- ===================================================== -->

    <aside
      :class="[
        'sidebar shadow-sm transition',
        sidebarOpen ? 'open' : ''
      ]"
    >

      <!-- LOGO / NOM CLINIQUE -->
      <div class="logo text-center mb-4">
        <div class="d-flex align-items-center justify-content-center gap-2 entete-clinique">
          <img v-if="logoClinique && !logoEnErreur" :src="logoClinique" alt="" class="logo-clinique" @error="logoEnErreur = true">
          <p class="h3 nom-clinique text-gradient fw-bold mb-0">
            {{ $store.getters.isPlatformAdmin ? marque.nom : (nomClinique || marque.nom) }}
          </p>
        </div>
        <small class="text-muted marque-signature" :title="`${marque.slogan} — ${marque.signature}`">{{ $store.getters.isPlatformAdmin ? 'Administration de la plateforme' : marque.nom }}</small>
      </div>

      <nav class="p-2" aria-label="Menu principal">
        <ul class="nav flex-column">

          <!-- Administrateur de la plateforme -->
          <template v-if="$store.getters.isPlatformAdmin">
            <li class="nav-item" v-for="lien in menuPlateforme" :key="lien.to">
              <router-link :to="lien.to" class="nav-link" exact-active-class="active-exact" @click="navigateAndClose(lien.to)">
                <i class="bi me-2" :class="lien.icone"></i>{{ lien.libelle }}
              </router-link>
            </li>
          </template>

          <!-- Clinique : menu construit depuis src/navigation/menu.js (droits + offre) -->
          <template v-else>
            <li v-for="s in menu" :key="s.cle" class="nav-item">
              <router-link v-if="s.to" :to="s.to" class="nav-link" exact-active-class="active-exact" :class="{ active: sectionActive === s.cle }" @click="navigateAndClose(s.to)">
                <i class="bi me-2" :class="s.icone"></i>{{ s.libelle }}
              </router-link>
              <template v-else>
                <a class="nav-link d-flex justify-content-between align-items-center" href="#" role="button" :aria-expanded="openedCollapse === s.cle" :class="{ active: sectionActive === s.cle }" @click.prevent="toggleCollapse(s.cle)">
                  <span><i class="bi me-2" :class="s.icone"></i>{{ s.libelle }}</span>
                  <i class="bi" :class="openedCollapse === s.cle ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
                </a>
                <ul v-show="openedCollapse === s.cle" class="submenu list-unstyled">
                  <li v-for="lien in s.liens" :key="lien.to" class="ms-1">
                    <router-link :to="lien.to" class="nav-link" exact-active-class="active-exact" @click="navigateAndClose(lien.to)">{{ lien.libelle }}</router-link>
                  </li>
                </ul>
              </template>
            </li>
          </template>

        </ul>
      </nav>

    </aside>


    <!-- ===================================================== -->
    <!-- OVERLAY MOBILE -->
    <!-- ===================================================== -->

    <div
      v-if="sidebarOpen"
      class="overlay d-md-none"
      @click="toggleSidebar"
    ></div>


    <!-- ===================================================== -->
    <!-- CONTENU -->
    <!-- ===================================================== -->

    <section class="content">

      <header
        class="header d-flex align-items-center justify-content-between px-3"
      >

        <!-- MOBILE -->
        <button
          type="button"
          class="btn btn-outline-secondary d-md-none me-3 bouton-menu"
          aria-label="Ouvrir le menu"
          :aria-expanded="sidebarOpen"
          @click.stop="toggleSidebar"
        >
          <i class="bi bi-list" aria-hidden="true"></i>
        </button>


        <!-- SLOGAN -->
        <div
          class="slogan d-flex flex-column justify-content-center"
        >

          <h5 class="mb-0 text-primary fw-bold"></h5>

          <small class="text-muted"></small>

        </div>


        <!-- MA CAISSE (personnes qui encaissent) -->
        <ma-caisse v-if="peutEncaisser" class="me-2" />

        <!-- PROFIL -->
        <div class="dropdown">

          <button
            class="btn btn-light rounded-pill shadow-sm dropdown-toggle d-flex align-items-center"
            type="button"
            id="userDropdown"
            data-bs-toggle="dropdown"
          >

            <img
              :src="
                user?.photo ||
                'https://cdn-icons-png.flaticon.com/512/3774/3774299.png'
              "
              class="rounded-circle me-2"
              style="width:40px;height:40px"
            >

            <div
              class="text-start"
              v-if="currentUser"
            >

              <div class="fw-semibold">
                {{ currentUser.nom }}
                {{ currentUser.prenom }}
              </div>

              <small class="text-muted">
                {{ $store.getters.isPlatformAdmin ? 'Administrateur plateforme' : (currentUser.typeUser?.nom || currentUser.typeUser || 'Utilisateur') }}
              </small>

            </div>

          </button>


          <ul class="dropdown-menu dropdown-menu-end">

            <li>

              <router-link
                class="dropdown-item"
                :to="{ name: 'compte' }"
              >
                <i class="bi bi-person-circle me-2"></i>
                Mon compte
              </router-link>

            </li>

            <li>
              <router-link class="dropdown-item" :to="{ name: 'aide' }">
                <i class="bi bi-life-preserver me-2"></i>
                Guide d'utilisation
              </router-link>
            </li>


            <li>
              <hr class="dropdown-divider">
            </li>


            <li>

              <a
                class="dropdown-item text-danger"
                href="#"
                @click="logout"
              >
                <i class="bi bi-box-arrow-right me-1"></i>
                Déconnexion
              </a>

            </li>

          </ul>

        </div>

      </header>


      <main>

        <!-- Abonnement bientôt échu ou dépassé (période de grâce) -->
        <div
          v-if="alerteAbonnement"
          class="alert mb-0 rounded-0 py-2 px-3 d-flex align-items-center gap-2"
          :class="alerteAbonnement.classe"
          role="alert"
        >
          <i class="bi bi-exclamation-triangle-fill"></i>
          <span>{{ alerteAbonnement.message }}</span>
        </div>

        <!-- Après le passage en payant : choisir le sort des données de l'essai -->
        <div
          v-if="choixDonneesEssai"
          class="alert alert-info mb-0 rounded-0 py-2 px-3 d-flex align-items-center gap-2 flex-wrap"
          role="alert"
        >
          <i class="bi bi-question-circle-fill"></i>
          <span>
            Bienvenue dans votre abonnement ! Les données saisies pendant l'essai sont-elles réelles ou des tests ?
            Indiquez-le avant le {{ choixDonneesEssai }}.
          </span>
          <router-link :to="{ name: 'mon_abonnement' }" class="btn btn-sm btn-primary ms-auto">Choisir</router-link>
        </div>

        <!-- Configuration obligatoire pas terminée (responsable de la clinique, administrateur de la plateforme) -->
        <div
          v-if="demarrageRestant"
          class="alert alert-primary mb-0 rounded-0 py-2 px-3 d-flex align-items-center gap-2 flex-wrap"
          role="status"
        >
          <i class="bi bi-rocket-takeoff"></i>
          <span>Configuration à terminer : {{ demarrageRestant }} étape(s) obligatoire(s) avant de commencer.</span>
          <router-link :to="{ name: routeDemarrage }" class="btn btn-sm btn-primary ms-auto">Continuer</router-link>
        </div>

        <!-- Coupure de connexion, saisies hors connexion en attente -->
        <BandeauConnexion />

        <router-view />

      </main>

    </section>

  </div>
</template>


<script>

import api from "@/api";
import MaCaisse from "@/components/caisse/MaCaisse.vue";
import { menuVisible } from "@/navigation/menu";
import { MARQUE } from "@/marque";
import BandeauConnexion from "@/components/horsLigne/BandeauConnexion.vue";
import { demarrerRafraichissement, effacerDonneesLocales, peutTravaillerHorsLigne } from "@/horsLigne/reference";
import { compter, synchroniser } from "@/horsLigne/file";
import { etat as etatConnexion } from "@/horsLigne/etat";
import { configClinique, urlFichier } from "@/impression/impression";
import { surveillerInactivite, arreterSurveillance, DUREE_MINUTES } from "@/surveillance/inactivite";
import { chargerDemarrage, etatDemarrage, routeDemarrage } from "@/navigation/demarrage";

export default {

  name: "MainLayout",

  components: { MaCaisse, BandeauConnexion },

  data() {

    return {
      marque: MARQUE,

      sidebarOpen: false,

      openedCollapse: null,

      nomClinique: null,

      configuration: null,
      logoEnErreur: false

    };

  },


  computed: {
    /** Étapes obligatoires des premiers pas restantes (bandeau masqué sur la page elle-même). */
    demarrageRestant() {
      if (['premiers_pas', 'plateforme_demarrage'].includes(this.$route.name)) return 0;
      return etatDemarrage.donnees?.resume?.obligatoiresRestantes || 0;
    },
    routeDemarrage() {
      return routeDemarrage();
    },
    /** Logo de la clinique (Administration → Configuration de la clinique), à côté de son nom. */
    logoClinique() {
      if (this.$store.getters.isPlatformAdmin || !this.configuration?.logo) return null;
      return urlFichier(this.configuration.logo);
    },

    /* =======================================================
       UTILISATEUR
    ======================================================= */

    currentUser() {

      return this.$store.getters.user;

    },


    /* Bouton « Ma caisse » : personnes qui encaissent */
    peutEncaisser() {
      const g = this.$store.getters;
      return !g.isPlatformAdmin && g.hasAnyPermission(['CAISSE_OUVRIR', 'CONSULT_PAIEMENT', 'HOSP_PAIEMENT', 'VIEW_AUTRES_SERVICES_FACTURE_PATIENT', 'NOUVELLE_VENTE', 'PAIEMENT_EN_LIGNE', 'DEPENSES_DECAISSER']);
    },

    /* Menu de la clinique (droits de l'utilisateur et modules de l'offre) */
    menu() {
      return menuVisible({ router: this.$router, getters: this.$store.getters, horsLigne: peutTravaillerHorsLigne });
    },

    /* Section du menu qui contient la page ouverte */
    sectionActive() {
      const chemin = this.$route.path;
      const correspond = (to) => to === '/' ? chemin === '/' : (chemin === to || chemin.startsWith(to + '/'));
      const s = this.menu.find(x => x.to ? correspond(x.to) : x.liens.some(l => correspond(l.to)));
      return s ? s.cle : null;
    },

    /* Menu de l'administrateur de la plateforme */
    menuPlateforme() {
      // Gestionnaire de la plateforme : les cliniques et le guide seulement
      if (this.$store.getters.isPlatformGestionnaire) {
        return [
          { to: '/plateforme/cliniques', icone: 'bi-buildings', libelle: 'Cliniques clientes' },
          { to: '/plateforme/factures', icone: 'bi-receipt', libelle: 'Factures' },
          { to: '/aide', icone: 'bi-life-preserver', libelle: "Guide d'utilisation" }
        ];
      }
      return [
        { to: '/plateforme/demarrage', icone: 'bi-rocket-takeoff', libelle: 'Démarrage' },
        { to: '/plateforme/revenus', icone: 'bi-graph-up-arrow', libelle: "Chiffre d'affaires" },
        { to: '/plateforme/cliniques', icone: 'bi-buildings', libelle: 'Cliniques clientes' },
        { to: '/plateforme/factures', icone: 'bi-receipt', libelle: 'Factures' },
        { to: '/plateforme/offres', icone: 'bi-boxes', libelle: 'Offres' },
        { to: '/plateforme/apporteurs', icone: 'bi-person-badge', libelle: "Apporteurs d'affaires" },
        { to: '/plateforme/encaissement', icone: 'bi-sliders', libelle: 'Paramètres et inscriptions' },
        { to: '/plateforme/supervision', icone: 'bi-activity', libelle: 'Supervision' },
        { to: '/aide', icone: 'bi-life-preserver', libelle: "Guide d'utilisation" }
      ];
    },


    /* Données de l'essai à qualifier (réelles ou tests) : seulement pour qui gère l'abonnement */
    choixDonneesEssai() {
      const d = this.$store.getters.user?.clinique?.donneesEssai;
      if (!d || !d.aChoisir || !this.$store.getters.hasPermission('ABONNEMENT_GERER')) {
        return null;
      }
      if (this.$route.name === 'mon_abonnement') {
        return null;
      }
      const [a, m, j] = String(d.dateLimite).split('-');
      return `${j}/${m}/${a}`;
    },

    /* Alerte d'abonnement : 7 jours avant l'échéance, puis pendant la période de grâce */
    alerteAbonnement() {
      const clinique = this.$store.getters.user?.clinique;
      const jours = clinique?.joursRestants;

      if (jours === null || jours === undefined || jours > 7) {
        return null;
      }

      // Non-renouvellement demandé : rappeler d'exporter les données
      if (clinique?.resiliation) {
        return {
          classe: jours < 0 ? 'alert-danger' : 'alert-warning',
          message: jours < 0
            ? `Votre abonnement est terminé (non renouvelé). L'accès sera bloqué dans ${Math.max(7 + jours, 0)} jour(s) : exportez vos données depuis « Mon abonnement ».`
            : `Votre abonnement se termine dans ${jours} jour(s) et ne sera pas renouvelé. Exportez vos données depuis « Mon abonnement », ou changez d'avis.`
        };
      }

      if (jours < 0) {
        const grace = 7 + jours;
        return {
          classe: 'alert-danger',
          message: `L'abonnement de votre clinique a expiré. L'accès sera bloqué dans ${Math.max(grace, 0)} jour(s) : renouvelez-le depuis « Mon abonnement ».`
        };
      }

      return {
        classe: 'alert-warning',
        message: jours === 0
          ? "L'abonnement de votre clinique se termine aujourd'hui. Pensez à le renouveler."
          : `L'abonnement de votre clinique se termine dans ${jours} jour(s). Pensez à le renouveler depuis « Mon abonnement ».`
      };
    },


    user() {

      return this.$store.getters.user;

    },


    /* =======================================================
       SECTION ACTIVE
       Source unique de vérité : meta.section défini dans router.js.
       Dérivé directement de la route matchée par Vue Router,
       donc toujours correct, y compris juste après un F5.
    ======================================================= */

    activeSection() {

      return this.$route.meta?.section || null;

    },


    /* =======================================================
       ROLES
    ======================================================= */

    hasRole() {

      return this.$store.getters.hasRole;

    },


    hasAnyRole() {

      return this.$store.getters.hasAnyRole;

    }

  },


  /* =========================================================
     INITIALISATION
  ========================================================= */

  async created() {

    // Mode hors connexion : données de référence gardées sur le poste,
    // et envoi des saisies restées en attente
    if (!this.$store.getters.isPlatformAdmin) {
      demarrerRafraichissement(api);
      configClinique(true); // en-tête des impressions, gardé pour les coupures
      compter().then(() => { if (etatConnexion.enAttente > 0) synchroniser(); });
    }

    // Poste laissé sans activité : déconnexion (protection des dossiers patients)
    surveillerInactivite(() => this.deconnexionInactivite());

    await this.fetchNomClinique();

    this.restoreOpenedCollapse();

  },

  beforeUnmount() {
    arreterSurveillance();
  },


  /*
   * Quand Vue Router change de route sans recréer MainLayout,
   * on rouvre automatiquement le bon sous-menu.
   */

  watch: {

    "$route.path": {

      immediate: true,

      handler() {

        this.restoreOpenedCollapse();

        // Écran étroit : le menu se referme après avoir choisi une page
        if (window.innerWidth < 768) this.sidebarOpen = false;

        // Une étape vient peut-être d'être faite : le bandeau se met à jour
        chargerDemarrage();

      }

    }

  },


  methods: {


    /* =======================================================
       CONFIGURATION CLINIQUE
    ======================================================= */

    async fetchNomClinique() {

      try {

        const res =
          await api.getItems("configurations/entete");

        this.nomClinique =
          res.data.nom;

        this.configuration =
          res.data;

      }

      catch (e) {

        console.error(
          "Erreur récupération configuration",
          e
        );

        this.nomClinique =
          "CLINIQUE";

      }

    },


    /* =======================================================
       OUVRIR LE BON SOUS-MENU
       Se base uniquement sur meta.section : plus besoin de lister
       les chemins à la main, plus de risque de désynchronisation.
    ======================================================= */

    restoreOpenedCollapse() {

      this.openedCollapse = this.sectionActive;

    },


    /* =======================================================
       OUVERTURE / FERMETURE DROPDOWN
    ======================================================= */

    toggleCollapse(name) {

      this.openedCollapse =
        this.openedCollapse === name ? null : name;

    },


    /* =======================================================
       NAVIGATION
    ======================================================= */

    navigateAndClose(link) {

      if (window.innerWidth < 768) {

        this.sidebarOpen = false;

        setTimeout(() => {

          this.$router.push(link);

        }, 200);

      }

      else {

        this.$router.push(link);

      }

    },


    /* =======================================================
       SIDEBAR MOBILE
    ======================================================= */

    toggleSidebar() {

      this.sidebarOpen =
        !this.sidebarOpen;

    },


    /* =======================================================
       LOGOUT
    ======================================================= */

    async deconnexionInactivite() {
      // Les saisies hors connexion restent sur le poste (envoyées à la prochaine connexion)
      await effacerDonneesLocales();
      this.$store.dispatch("logout");
      try { sessionStorage.setItem("keneya.messageConnexion", `Vous avez été déconnecté après ${DUREE_MINUTES} minutes sans activité, pour protéger les dossiers des patients.`); } catch (e) { /* sans stockage */ }
      this.$router.push({ path: "/login" });
    },

    async logout() {

      // Saisies faites hors connexion pas encore envoyées : on prévient avant
      if (etatConnexion.enAttente > 0) {
        const ok = confirm(`${etatConnexion.enAttente} saisie(s) faite(s) hors connexion ne sont pas encore envoyées au serveur. Elles restent sur ce poste et partiront à votre prochaine connexion. Vous déconnecter quand même ?`);
        if (!ok) return;
      }

      // Plus aucune donnée de patient gardée sur le poste
      await effacerDonneesLocales();

      this.$store.dispatch("logout");

      this.$router.push("/login");

    }

  }

};

</script>


<style scoped>

.main-layout {

  display: flex;

  min-height: 100vh;

  background: #f8fafd;

}


/* =========================================================
   SIDEBAR
========================================================= */

.sidebar {

  width: 300px;

  background: #fff;

  padding: 1rem;

  position: fixed;

  top: 0;

  left: 0;

  bottom: 0;

  overflow-y: auto;

  border-radius: 16px 0 0 16px;

  box-shadow:
    2px 0 12px rgba(0, 0, 0, 0.05);

  transition:
    transform 0.3s ease,
    width 0.3s ease;

  z-index: 1030;

}


@media(max-width:767px) {

  /* Menu replié hors de l'écran ; ouvert par le bouton ☰, au-dessus du contenu et du voile */
  .sidebar {

    transform: translateX(-105%);

    width: min(300px, 85vw);

    border-radius: 0 16px 16px 0;

    z-index: 1060;

  }

  .sidebar.open {

    transform: translateX(0) !important;

  }

}


/* =========================================================
   LIENS SIDEBAR
========================================================= */

.sidebar .nav-link {

  color: #1565c0;

  margin-bottom: 6px;

  border-radius: 10px;

  padding: 10px 12px;

  transition: all 0.2s ease;

  display: flex;

  align-items: center;

  cursor: pointer;

}


/* =========================================================
   ROUTE ACTIVE
========================================================= */

.sidebar .nav-link.active,

.sidebar .nav-link.active-exact {

  background:
    linear-gradient(
      90deg,
      #0d6efd,
      #6610f2
    );

  color: #fff !important;

  font-weight: 600;

}


/* =========================================================
   HOVER
========================================================= */

.sidebar .nav-link:hover {

  background:
    rgba(13, 110, 253, 0.08);

}


/*
 * IMPORTANT :
 * Quand le lien parent est actif,
 * le hover ne doit pas supprimer son fond.
 */

.sidebar .nav-link.active:hover,

.sidebar .nav-link.active-exact:hover {

  background:
    linear-gradient(
      90deg,
      #0d6efd,
      #6610f2
    );

  color: #fff !important;

}


/* =========================================================
   SOUS-MENU
========================================================= */

.submenu {

  padding-left: 15px;

  margin-top: 5px;

  transition:
    max-height 0.3s ease;

  overflow: hidden;

}


.submenu .nav-link {

  font-size: 0.92rem;

}


/* =========================================================
   LIEN ACTIF DU SOUS-MENU
========================================================= */

.submenu .nav-link.router-link-active,

.submenu .nav-link.router-link-exact-active,

.submenu .nav-link.active-exact {

  background:
    rgba(13, 110, 253, 0.1);

  border-left:
    3px solid #0d6efd;

  color:
    #0d6efd !important;

  font-weight:
    600;

  border-radius:
    8px;

}


/* =========================================================
   OVERLAY MOBILE
========================================================= */

.overlay {

  position: fixed;

  top: 0;

  left: 0;

  right: 0;

  bottom: 0;

  background:
    rgba(0,0,0,0.4);

  z-index: 1020;

}


/* =========================================================
   CONTENU
========================================================= */

.content {

  flex: 1;

  margin-left: 280px;

  display: flex;

  flex-direction: column;

  min-height: 100vh;

  overflow-x: hidden;

  overflow-y: auto;

  transition:
    margin-left 0.3s ease;

}


@media (max-width: 767px) {

  .content {

    margin-left: 0;

  }

}


/* =========================================================
   HEADER
========================================================= */

.header {

  position: relative;

  height: 70px;

  background: #fff;

  border-bottom:
    1px solid #e3f2fd;

  display: flex;

  align-items: center;

  justify-content: space-between;

  padding: 0 1rem;

  z-index: 1040;

  flex-shrink: 0;

}


.header .slogan h5 {

  font-size: 1.1rem;

  color: #0d6efd;

  font-weight: 600;

}


.header .slogan small {

  font-size: 0.8rem;

  color: #6c757d;

}


/* =========================================================
   MAIN
========================================================= */

main {

  flex: 1;

  overflow-y: auto;

  padding: 1rem;

}


/* =========================================================
   NOM CLINIQUE
========================================================= */

.logo-clinique {
  max-height: 44px;
  max-width: 64px;
  object-fit: contain;
  border-radius: 8px;
  flex-shrink: 0;
}

.entete-clinique .nom-clinique {
  word-break: break-word;
}

.nom-clinique {

  background:
    linear-gradient(
      90deg,
      #0d6efd,
      #6610f2
    );

  -webkit-background-clip: text;

  -webkit-text-fill-color: transparent;

  transition: all 0.3s ease;

  cursor: default;

}


.nom-clinique:hover {

  transform:
    scale(1.05);

  text-shadow:
    0 4px 12px rgba(0, 0, 0, 0.2);

}

</style>
